import React, { useContext, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, Search, Filter, BookOpen } from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getStudents } from "../../services/students";
import { getMyTeacherProfile } from "../../services/teachers";
import Pagination from "../../components/common/Pagination";

const TeacherStudents: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
    }
  };

  // Fetch teacher profile to get assigned classes
  const { data: teacherData, isLoading: teacherLoading } = useQuery({
    queryKey: ["teacher-profile", user?.id],
    queryFn: () => getMyTeacherProfile(),
    enabled: !!user?.id,
  });

  // Fetch all students
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ["teacher-students", user?.id],
    queryFn: () => getStudents(),
    enabled: !!user?.id,
  });

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearchTerm, selectedClass]);

  if (teacherLoading || studentsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-emerald-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading students...</p>
        </div>
      </div>
    );
  }

  const teacher = teacherData?.data;
  const allStudents = studentsData?.data || [];

  // Get teacher's assigned classes and courses (backend returns 'classes' not 'currentClasses')
  const teacherClasses = teacher?.classes || teacher?.currentClasses || [];

  const teacherClassIds = teacherClasses.map(
    (cls: any) => cls.classId?._id || cls.classId,
  );
  const teacherCourseIds = teacherClasses.flatMap(
    (cls: any) => cls.courses?.map((course: any) => course._id || course) || [],
  );

  // Filter students to only show those enrolled in teacher's courses
  const teacherStudents = allStudents.filter((student: any) => {
    // Handle both ObjectId and string comparisons
    const studentCourses = student.courses || student.coursesInfo || [];
    const studentCourseIds = studentCourses.map((c: any) => c._id || c);

    const hasMatchingCourse = studentCourseIds.some((courseId: string) =>
      teacherCourseIds.some(
        (teacherCourseId: string) =>
          courseId.toString() === teacherCourseId.toString(),
      ),
    );

    // Also check if student is in any of teacher's classes
    const studentClassId =
      student.currentClassId || student.classInfo?.[0]?._id;
    const isInTeacherClass = teacherClassIds.some(
      (classId: string) => classId.toString() === studentClassId?.toString(),
    );

    return hasMatchingCourse || isInTeacherClass;
  });

  // Filter students based on search and class selection
  const filteredStudents = teacherStudents.filter((student: any) => {
    const matchesSearch =
      !activeSearchTerm ||
      (student.userInfo?.firstName || student.firstName || "")
        .toLowerCase()
        .includes(activeSearchTerm.toLowerCase()) ||
      (student.userInfo?.lastName || student.lastName || "")
        .toLowerCase()
        .includes(activeSearchTerm.toLowerCase()) ||
      (student.studentId || "")
        .toLowerCase()
        .includes(activeSearchTerm.toLowerCase());

    const studentClassId =
      student.currentClassId || student.classInfo?.[0]?._id;
    const matchesClass =
      !selectedClass || studentClassId?.toString() === selectedClass.toString();

    return matchesSearch && matchesClass;
  });

  // Pagination logic
  const totalStudents = filteredStudents.length;
  const totalPages = Math.ceil(totalStudents / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* Header */}
      <div className="mb-8 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              My Students
            </h1>
            <p className="text-slate-600 font-medium">
              View students enrolled in your courses and classes
            </p>
          </div>
          <div className="hidden md:flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 shadow-lg">
            <Users className="h-8 w-8 text-white" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6 mb-8">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center shadow-md mr-3">
            <Filter className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">Filter Students</h3>
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Search by Name or Admission Number
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Type and press Enter to search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium bg-white shadow-sm transition-all"
              />
            </div>
          </div>

          {/* Class Filter */}
          <div className="md:w-80">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Filter by Class
            </label>
            <div className="relative">
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none font-medium bg-white shadow-sm transition-all"
              >
                <option value="">All My Classes</option>
                {teacherClasses.map((classAssignment: any) => {
                  const classId =
                    classAssignment.classId?._id || classAssignment.classId;
                  const className =
                    classAssignment.classId?.name || "Unknown Class";
                  return (
                    <option key={classId} value={classId}>
                      {className}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Students Grid */}
      {paginatedStudents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {paginatedStudents.map((student: any) => {
            const studentClassId =
              student.currentClassId || student.classInfo?.[0]?._id;
            const studentClass = teacherClasses.find((classAssignment: any) => {
              const classId =
                classAssignment.classId?._id || classAssignment.classId;
              return classId?.toString() === studentClassId?.toString();
            });

            return (
              <div
                key={student._id}
                className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6 hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
              >
                {/* Student Header with Avatar and Status */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center flex-1">
                    {/* Profile Avatar */}
                    <div className="relative">
                      {student.photoUrl ||
                      student.userInfo?.profileDetails?.profilePicture ? (
                        <img
                          src={
                            student.photoUrl ||
                            student.userInfo?.profileDetails?.profilePicture
                          }
                          alt={`${
                            student.userInfo?.firstName || student.firstName
                          } ${student.userInfo?.lastName || student.lastName}`}
                          className="w-14 h-14 rounded-xl object-cover shadow-md"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                            (
                              e.target as HTMLImageElement
                            ).nextElementSibling?.classList.remove("hidden");
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-14 h-14 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-md ${
                          student.photoUrl ||
                          student.userInfo?.profileDetails?.profilePicture
                            ? "hidden"
                            : ""
                        }`}
                      >
                        <span className="text-white font-bold text-lg">
                          {(
                            student.userInfo?.firstName ||
                            student.firstName ||
                            "S"
                          )?.charAt(0)}
                          {(
                            student.userInfo?.lastName ||
                            student.lastName ||
                            "S"
                          )?.charAt(0)}
                        </span>
                      </div>
                    </div>

                    {/* Student Info */}
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-1">
                        {student.userInfo?.firstName || student.firstName}{" "}
                        {student.userInfo?.lastName || student.lastName}
                      </h3>
                      <div className="space-y-1">
                        <p className="text-sm text-slate-600 font-medium">
                          <span className="inline-block w-20 text-slate-500">
                            Admission:
                          </span>
                          {student.studentId}
                        </p>
                        {studentClass && (
                          <p className="text-sm text-slate-600 font-medium">
                            <span className="inline-block w-20 text-slate-500">
                              Course:
                            </span>
                            {typeof studentClass.classId === "object" &&
                            studentClass.classId?.name
                              ? studentClass.classId.name
                              : "Unknown Class"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="ml-4">
                    <span
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-full shadow-sm ${
                        student.academicStatus === "active"
                          ? "bg-gradient-to-r from-emerald-400 to-emerald-500 text-white"
                          : "bg-gradient-to-r from-slate-400 to-slate-500 text-white"
                      }`}
                    >
                      {student.academicStatus === "active"
                        ? "Active"
                        : student.academicStatus || "Active"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mx-auto shadow-lg mb-6">
            <Users className="h-10 w-10 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-3">
            {searchTerm || selectedClass
              ? "No students found"
              : "No Students Yet"}
          </h3>
          <p className="text-slate-600 font-medium max-w-md mx-auto mb-6 leading-relaxed">
            {searchTerm || selectedClass
              ? "Try adjusting your search criteria or filters to find the students you're looking for."
              : "Students enrolled in your courses will appear here once they're assigned."}
          </p>
          {(searchTerm || selectedClass) && (
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedClass("");
              }}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-400 to-indigo-500 text-white text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 shadow-md"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      <div
        className={`mt-8 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6 ${filteredStudents.length === 0 ? "hidden" : ""}`}
      >
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={itemsPerPage}
          onPageSizeChange={setItemsPerPage}
          showPageSizeSelector={true}
          className="w-full"
        />
      </div>

      {/* Summary */}
      <div
        className={`mt-8 bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl border border-slate-200/60 shadow-lg p-4 ${filteredStudents.length === 0 ? "hidden" : ""}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mr-3">
              <Users className="h-4 w-4 text-white" />
            </div>
            <p className="text-sm text-slate-700 font-semibold">
              Showing {startIndex + 1}-{Math.min(endIndex, totalStudents)} of{" "}
              {totalStudents} students
              {selectedClass &&
                ` in ${(() => {
                  const foundClass = teacherClasses.find(
                    (classAssignment: any) => {
                      const classId =
                        classAssignment.classId?._id || classAssignment.classId;
                      return classId?.toString() === selectedClass.toString();
                    },
                  );
                  return foundClass &&
                    typeof foundClass.classId === "object" &&
                    foundClass.classId?.name
                    ? foundClass.classId.name
                    : "selected class";
                })()}`}
            </p>
          </div>
          <div className="text-xs text-slate-500 font-medium">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherStudents;
